import {
  Component,
  OnInit,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  AfterViewChecked,
  inject,
  PLATFORM_ID
} from '@angular/core';
import { CommonModule , isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, NgForm, Validators } from '@angular/forms';
import { CodeInputComponent, CodeInputModule } from 'angular-code-input';
import { NgxMaskDirective } from 'ngx-mask';
import { DictionaryService, SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { InstaTouchState, InstaTouchSession } from '@shared/model/instatouch.model';
import { LenderService } from '@shared/service/lender/lender.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { FieldInstance } from '@workflow/class/field.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';
import { PageInstance } from '@workflow/class/page.service';
import { EcpSharedModule } from '@workflow/ecp-shared/shared.module';

/**
 * TODO:
 * Test that upon successful verify call made on prefill verify page, that vfp is in stateful value
 * Change error being thrown for alloy prefill when requesting new link
 * Wire up logic for OLA
 * Address personalInfoAutofill/passcodeErrorMessage for alloy.
 */

@Component({
  selector: 'dx-prefill',
  templateUrl: './prefill.component.html',
  styleUrls: ['./prefill.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedUiLibraryModule, CodeInputModule, EcpSharedModule, NgxMaskDirective]
})
export class PrefillComponent implements OnInit, OnChanges, AfterViewChecked {
  // Using inject pattern for dependencies
  private readonly dictionary = inject(DictionaryService);
  private readonly lenderService = inject(LenderService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly workflowService = inject(WorkflowService);
  public readonly applicationApiService = inject(ApplicationApiService);
  private readonly analyticsService = inject(AnalyticsMetadataService);
  private readonly googleTrackingService = inject(GoogleTrackingService);

  @ViewChild('codeInput') codeInput: CodeInputComponent;
  @ViewChild('instaTouch') instaTouch: NgForm;
  /** Input to indicate Instatouch buuilding block field */
  @Input() public autofillField: FieldInstance;
  @Input() public instaTouchState: InstaTouchState;
  @Input() public lockMobileNumber = false;
  @Input() public errorMessage: string;
  /** flag to indicate Api call in progress */
  @Input() public isLoading: boolean;
  @Input() public showCodeSentAlert?: boolean;
  @Output() public instaTouchStateChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public getPassCode: EventEmitter<InstaTouchSession> = new EventEmitter<InstaTouchSession>();
  @Output() public validatePassCode: EventEmitter<InstaTouchSession> = new EventEmitter<InstaTouchSession>();
  @Output() public getPersonalInfo: EventEmitter<InstaTouchSession> = new EventEmitter<InstaTouchSession>();
  /** resend passcode (Alloy specific path will be handled by parent) */
  @Output() public resendPassCode: EventEmitter<InstaTouchSession> = new EventEmitter<InstaTouchSession>();
  /** emit event to show manual card or alloy skip metadata */
  @Output() public proceedWithoutPhone = new EventEmitter<{ applicantIndex?: number }>();
  /** fiel ref for mobile number */
  @ViewChild('input') public input: ElementRef;
  /** Mobile number input field */
  @Input() public mobileNumber: string;
  /** ssnNumber input field*/
  public ssnNumber: number;
  /** zipCode input field */
  public zipCode: number;
  /** Passcode input field */
  public passcode: string;
  public showNewCodeSentAlert = false;
  public isCoapplicant = false;
  public isSecondCoapplicant = false;
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  /**
   * Whether Alloy prefill is enabled (derived live from lenderService so it stays reactive
   * without needing manual synchronization). Exposed as a public getter so templates and
   * methods can bind to it just like a property.
   */

  constructor() {
    const page = this.workflowService.currentPage;
    // Set if the instance is for a co-applicant
    // This works for both OLA and OAO
    this.isCoapplicant = page?.config?.id?.toLowerCase().includes('co-applicant');
    this.isSecondCoapplicant = page?.config?.id?.toLowerCase().includes('additional-co-app');
  }

  public readonly stages: { [key: string]: InstaTouchState } = {
    initial: 'initial',
    passcode: 'passcode',
    identity: 'identity',
    completed: 'completed'
  };

  /**
   * 1 = primary, 2 = first co-applicant, 3 = second co-applicant
   */
  get applicantIndex(): number {
    if (this.isSecondCoapplicant) {
      return 3;
    }
    if (this.isCoapplicant) {
      return 2;
    }
    return 1;
  }

  public get isAlloyPrefill(): boolean {
    return this.lenderService?.lenderData?.enableAlloyPrefill;
  }

  /** get currentStage */
  public get currentStage(): InstaTouchState {
    return this.instaTouchState;
  }

  /** set currentStage */
  public set currentStage(value) {
    this.instaTouchState = value;
    this.instaTouchStateChange.emit(value);
  }

  /** return mobile number without Mask */
  public get unMaskedNumber(): string {
    return this.mobileNumber ? this.mobileNumber.replace(/[-() ]/g, '') : '';
  }

  /** mobile number length */
  public get validMobileNumber(): boolean {
    const length = 10;
    return this.unMaskedNumber.length === length;
  }

  /** methods to display lenderName on disclaimer */
  public get disclaimer(): string {
    const lenderName = this.lenderService?.lenderData?.lenderName;
    return this.dictionary.read('personalInfoAutofill/disclaimer')?.replace(/{lenderName}/gi, lenderName);
  }

  /** getter prop to credit score info when ssn verification */
  public get creditScoreInfo(): string {
    return this.dictionary.read('personalInfoAutofill/creditScoreInfo');
  }

  /**
   * get identity error message
   */
  public get getIdentityErrorMessage(): string {
    return `${this.dictionary.read('personalInfoAutofill/identityError')}
    ${this.proceedWithoutPhone.observers?.length !== 0 ? this.dictionary.read('personalInfoAutofill/identityErrorAction') : ''}.
    ${this.dictionary.read('personalInfoAutofill/Offramp2')}`;
  }

  public ngOnInit() {
    this.lockMobileNumber = this.lockMobileNumber && this.validMobileNumber;
    this.reset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.instaTouchState) {
      this.scrolltoTop();
    }
    // clear passcode input if error message is present
    if (this.currentStage === this.stages.passcode && this.errorMessage) {
      this.clearCodeInput();
    }
  }

  public ngAfterViewChecked(): void {
    if (this.currentStage === this.stages.passcode) {
      this.verificationInputWCAG();
    }
  }
  public reset(): void {
    // Alloy restore scenario: if parent has already advanced (e.g., to passcode) preserve it.
    // Non-Alloy (Instatouch) always resets to initial for a clean start.
    if (this.isAlloyPrefill) {
      if (!this.instaTouchState || this.instaTouchState === this.stages.initial) {
        this.currentStage = this.stages.initial;
      }
    } else {
      this.currentStage = this.stages.initial;
    }
    this.isLoading = false;
    this.errorMessage = null;
  }

  private scrolltoTop(): void {
    if (this.isBrowser) {
      window.scrollTo(0, 0);
    }
  }

  /**
   * Show the mobile number with asterisks format to user.
   */
  public getMobileNumberWithAsterisks(mobileNumber: string): string {
    return mobileNumber && '(***) ***-' + mobileNumber.substring(mobileNumber.length - 4);
  }
  /**
   * Method to set WCAG attributes for verification code input fields.
   */
  private verificationInputWCAG(): void {
    if (!this.codeInput) return;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    for (let i = 0; i < this.codeInput['inputs']?.length; i++) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].id = 'code' + (i + 1);
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].setAttribute('aria-label', 'Digit ' + (i + 1) + ' of 6 digit verification code');
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].setAttribute('aria-describedby', 'code_error');
    }
  }

  public clearCodeInput(): void {
    if (!this.codeInput) return;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    for (let i = 0; i < this.codeInput['inputs']?.length; i++) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].value = '';
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].className = '';
    }
    setTimeout(() => {
      this.codeInput.inputsList?.first?.nativeElement?.focus();
    }, 500);
  }

  /**
   * Fired every time the code input changes.
   * @param e the current code entry
   */
  public onCodeChanged(e) {
    this.passcode = e;
    if (!this.instaTouch?.form?.controls?.codeInput) {
      // Programatically add a passcode field into ngform
      this.instaTouch?.form?.addControl(
        'codeInput',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.formBuilder.control('', [Validators.required, Validators.maxLength(6), Validators.minLength(6)])
      );
    }
    this.instaTouch?.form?.controls?.codeInput.setValue(this.passcode);
  }

  /**
   * Fires once the code input is complete.
   * @param e the current code entry
   */
  public onCodeCompleted(e) {
    this.passcode = e;
    this.emitEvent(this.instaTouch);
  }

  public emitEvent(ngForm: NgForm): void {
    ngForm.form.markAllAsTouched();
    if (ngForm.invalid) return;
    const data = {
      isPrimaryApplicant: !this.isCoapplicant && !this.isSecondCoapplicant
    } as InstaTouchSession;
    switch (this.currentStage) {
      case this.stages.initial:
        data.mobileNumber = this.unMaskedNumber;
        if (this.isAlloyPrefill) {
          data.ssn = this.ssnNumber;
          data.applicantIndex = this.applicantIndex;
        }
        const metadata = this.analyticsService.getMetadata({
          title: this.isAlloyPrefill ? 'Alloy - Start' : 'Instatouch - Start',
          id: this.isAlloyPrefill ? 'alloy-prefill-page' : 'instatouch-page'
        } as PageInstance);
        this.googleTrackingService.publishEvent(metadata[0], metadata[1]);
        this.getPassCode.emit(data);
        break;
      case this.stages.passcode:
        data.passCode = this.passcode;
        this.validatePassCode.emit(data);
        break;
      case this.stages.identity:
        data.ssn = this.ssnNumber;
        data.zipCode = this.zipCode;
        const metadata2 = this.analyticsService.getMetadata({
          title: this.isAlloyPrefill ? 'Alloy - End' : 'Instatouch - End',
          id: this.isAlloyPrefill ? 'alloy-page' : 'instatouch-page'
        } as PageInstance);
        this.googleTrackingService.publishEvent(metadata2[0], metadata2[1]);
        this.getPersonalInfo.emit(data);
        break;
    }
    this.showNewCodeSentAlert = false;
    ngForm.resetForm(ngForm.value);
  }

  public getNewPasscode(): void {
    const data = {
      isPrimaryApplicant: !this.isCoapplicant && !this.isSecondCoapplicant,
      mobileNumber: this.unMaskedNumber,
      applicantIndex: this.applicantIndex
    } as InstaTouchSession;
    if (this.isAlloyPrefill) {
      data.applicantIndex = this.applicantIndex;
    }

    this.clearCodeInput();
    this.showNewCodeSentAlert = true;
    // For Alloy we need to hit a different endpoint (resendPrefillLink) so emit a distinct event
    if (this.isAlloyPrefill) {
      this.resendPassCode.emit(data);
    } else {
      this.getPassCode.emit(data);
    }
  }

  /**
   * Emit the event when user clicks manual flow link option from insta touch card.
   */
  public proceedWithoutPhonePrefill(): void {
    if (this.isAlloyPrefill) {
      this.proceedWithoutPhone.emit({ applicantIndex: this.applicantIndex });
    } else {
      this.proceedWithoutPhone.emit({});
    }
  }

  private readonly prefillVariantKeys = new Set(['mobileHeader', 'description']);

  private buildDictionaryKey(base: string): string {
    let suffix = '';
    if (this.isSecondCoapplicant) {
      suffix = 'SecondCoapp';
    } else if (this.isCoapplicant) {
      suffix = 'Coapp';
    }
    // identityHeader* entries are not provider variants in flavor JSON
    if (this.prefillVariantKeys.has(base)) {
      const prefillType = this.isAlloyPrefill ? 'alloy' : 'instatouch';
      return `personalInfoAutofill/${base}${suffix}/${prefillType}`;
    }
    return `personalInfoAutofill/${base}${suffix}`;
  }

  public getHeadingLabel(label: string): string {
    return this.dictionary.read(this.buildDictionaryKey(label));
  }

  get cardDescription(): string {
    return this.dictionary.read(this.buildDictionaryKey('description'));
  }
}
